import { inventoryFetchClient } from "./inventoryFetchClient";
import { normalizeImageFile } from "@/helpers/normalizeImageFile";
import type { InventoryItemRow } from "@/types/custom-types";

type UploadUrlEntry = {
  uploadUrl?: string | null;
  id: string;
};

const WEBP_MIME = "image/webp";
const WEBP_EXTENSION = "webp";
const UPLOAD_BATCH_SIZE = 10;

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * Compress any image → WEBP, then upload to Wasabi via Rentmote getUploadUrls
 * with FileOriginOption.INVENTORY_INSPECTION (same prefix as manager: inventory_inspection/).
 */
export const uploadItemFiles = async (files: File[]): Promise<string[]> => {
  if (files.length === 0) return [];

  const normalizedFiles = await Promise.all(files.map(normalizeImageFile));
  const uploadedIds: string[] = [];

  for (const batch of chunkArray(normalizedFiles, UPLOAD_BATCH_SIZE)) {
    const uploadDataList = await inventoryFetchClient<UploadUrlEntry[]>(
      "/upload-urls",
      {
        method: "POST",
        operation: "getUploadUrls",
        variables: {
          inputs: batch.map((file) => ({
            contentType: WEBP_MIME,
            fileExtension: WEBP_EXTENSION,
            fileTypeOption: "PICTURE",
            fileOriginOption: "INVENTORY_INSPECTION",
            fileSize: file.size.toString(),
          })),
        },
      }
    );

    if (!uploadDataList || uploadDataList.length !== batch.length) {
      throw new Error(
        "No se pudieron obtener las URLs para subir las imágenes del inventario."
      );
    }

    await Promise.all(
      uploadDataList.map(async (uploadData, index) => {
        if (!uploadData.uploadUrl) {
          throw new Error("URL de subida no disponible");
        }

        const file = batch[index];
        let response: Response;
        try {
          response = await fetch(uploadData.uploadUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": WEBP_MIME },
          });
        } catch {
          throw new Error(
            `Failed to fetch al subir "${file.name}" a Wasabi (CORS o red). El origen del navegador tiene que estar en AllowedOrigins del bucket, sin barra final.`
          );
        }

        if (!response.ok) {
          throw new Error(
            `Error al subir "${file.name}" a almacenamiento (HTTP ${response.status}). Probá con una imagen más liviana o reintentá.`
          );
        }
      })
    );

    uploadedIds.push(...uploadDataList.map((entry) => entry.id));
  }

  return uploadedIds;
};

export type ItemFilePayload = {
  retainedFileIds: string[];
  uploadedFilesIds: string[];
};

/**
 * Matches manager InventoryFormModal: retained InventoryInspectionItemFile ids
 * + newly uploaded FileUpload ids.
 */
export const resolveItemFilePayload = async (
  row: InventoryItemRow
): Promise<ItemFilePayload | undefined> => {
  const hasNewFiles = (row.newFiles?.length ?? 0) > 0;
  const shouldProcessFiles = row.shouldReplaceFiles || hasNewFiles;

  if (!shouldProcessFiles) {
    return undefined;
  }

  return {
    retainedFileIds: (row.existingFiles ?? []).map((file) => file.id),
    uploadedFilesIds: await uploadItemFiles(row.newFiles ?? []),
  };
};
